// libraries
import * as React from 'react'
import { Col, OverlayTrigger, Row, Tooltip, Badge, Button } from 'react-bootstrap'
import { Link, useLocation } from 'react-router-dom'

// definitions
import { AccountXeroOrg, User, Calendar as CalendarData, Account } from '../../../../back-end/utilities/apiDefinitions'
import { ModalState } from './leavecal.d'
import * as XeroProducts from '../../constants/products'

// components
import ModalList from '../../components/ui/modalList/modalList'
import OnBoardModal from '../../components/ui/onBoardModal/onBoardModal'
import LeaveCalOnboardModal from '../../components/leavecal/onboardModal'
import BodyFrame, { FrameType } from '../../components/leavecal/frame'
import * as Icons from '../../components/ui/icons/icons'
import TermsAndConditionsModal from '../../components/common/termsAndConditionsModal'
import { TermsVersion } from '../../../../back-end/common/termsVersion'

// utilities
import * as I from '../../utilities/me'
import * as Format from '../../utilities/format'
import * as Request from '../../utilities/request'
import OrgCalPicker from '../../components/leavecal/OrgCalPicker'
import { IndentedDropdownSelector } from '../../components/leavecal/IndentedDropDown'
import { AppContext } from '../../App'
import { DateTime } from 'luxon'

const initialModalState: ModalState = {
	modal: '',
	props: {},
	size: 'sm',
}

const getUpdatedTs = (selectedCalendar: CalendarData, selectedOrg: AccountXeroOrg) => {
	// get the most recent date between calendar.fileLastUpdatedTs, org.leaveCalLastDataFetch and org.leaveCalLastEmployeeDataFetch
	const dates = [selectedCalendar.fileLastUpdatedTs, selectedOrg.xeroOrg?.leaveCalLastDataFetch, selectedOrg.xeroOrg?.leaveCalLastEmployeeDataFetch]
		.filter((x) => x)
		.map((x) => new Date(x!))
	const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())))
	return Format.dateTime(maxDate)
}

const getFrameFromHash = (hash: string): FrameType => {
	return ['calendar', 'emails', 'integration', 'settings', 'employees', 'leaveBalances', 'overview'].includes(hash) ? (hash as FrameType) : 'calendar'
}

const canAccountUseKeypay = (accounts: Account[], currentAccountID: string) => {
	if (accounts.length === 1) {
		return accounts[0].canUseKeypay!
	} else {
		const currentAccount = accounts.find((a) => a.accountID === currentAccountID)
		if (currentAccount) {
			return currentAccount!.canUseKeypay!
		} else {
			return false
		}
	}
}

const LeaveCal = () => {
	const { appState, updateAppState } = React.useContext(AppContext)
	const location = useLocation()

	const [orgs, updateOrgs] = React.useState<AccountXeroOrg[] | null>(null)
	const [, updateUsers] = React.useState<User[] | null>(null)
	const [calendars, updateCalendars] = React.useState<CalendarData[] | null>(null)
	const [xeroConsentUrl, updateXeroConsentUrl] = React.useState<string | null>(null)
	const [keypayConsentUrl, updateKeypayConsentUrl] = React.useState<string | null>(null)
	const [shownModal, updateShownModal] = React.useState<ModalState>(initialModalState)
	const [pendingModal, updatePendingModal] = React.useState<ModalState>(initialModalState)
	const [selectedOrg, updateSelectedOrg] = React.useState<AccountXeroOrg | null>(null)
	const [selectedCalendar, updateSelectedCalendar] = React.useState<CalendarData | null>(null)
	const [currentFrame, updateCurrentFrame] = React.useState<FrameType>(getFrameFromHash(location.hash.substring(1)))
	const [pageStatus, updatePageStatus] = React.useState<string>('Loading')

	const updateFrameAndHash = (frame: FrameType) => {
		window.location.hash = frame
		updateCurrentFrame(frame)
	}

	React.useEffect(() => {
		if (selectedOrg) {
			updateAppState({
				state: 'selectedOrg',
				data: selectedOrg,
			})
		}
	}, [selectedOrg]) // eslint-disable-line

	const refreshData = React.useCallback(
		async (org?: AccountXeroOrg, cal?: CalendarData) => {
			const fetchData = async () => {
				const [accountXeroOrgs, calendars, users, currentAccount, termsVersions, xeroUrl, keypayUrl] = await Promise.all(
					[
						Request.get('organisation?where=AccountXeroOrgIsLeaveCal==1', appState.authState),
						Request.get('calendar?where=AccountXeroOrgIsLeaveCal==1', appState.authState),
						Request.get('user', appState.authState),
						Request.get('account', appState.authState),
						Request.get('termsversion?where=TermsVersionIsLeaveCal==1', appState.authState),
					].concat(
						I.have('admin', appState.permission[appState.context])
							? [Request.get('xero/url/leavecal', appState.authState), Request.get('keypay/url', appState.authState)]
							: []
					)
				)

				updateOrgs(accountXeroOrgs.data.accountXeroOrgs)
				updateUsers(users.data.users)
				updateCalendars(calendars.data.calendars)
				const currentTermsVersion =
					termsVersions.data.termsVersions.length > 0
						? termsVersions.data.termsVersions.reduce((a: TermsVersion, b: TermsVersion) =>
								Math.abs(new Date(a.effectiveDate).getTime() - Date.now()) < Math.abs(new Date(b.effectiveDate).getTime() - Date.now()) ? a : b
						  )
						: null
				if (xeroUrl && xeroUrl.data.url) {
					updateXeroConsentUrl(xeroUrl.data.url)
				}
				if (keypayUrl && keypayUrl.data.url) {
					updateKeypayConsentUrl(keypayUrl.data.url)
				}

				// First check if this account has the current terms and consitions accepted. If yes, proceed, if not, ask them to accept. If denied also alert.
				let showTermsModal = false
				if (I.am(appState.permission[appState.context]) === 'Admin' && currentTermsVersion) {
					const currentAccountObj = currentAccount.data.accounts.find(
						(a: { accountID: string }) => a.accountID === appState!.authState!.attributes!['custom:currentAccountId']
					)

					if (currentAccountObj?.termsAcceptance && currentAccountObj.termsAcceptance.length > 0) {
						const latestAcceptance = currentAccountObj.termsAcceptance.find(
							(ta: { termsVersionID: string }) => ta.termsVersionID === currentTermsVersion.termsVersionID
						)
						if (latestAcceptance) {
							if (latestAcceptance.termsAcceptanceStatus === 'denied') {
								//someone has denied them so show message
								updateShownModal({
									modal: 'TermsAndConditionsAcceptance',
									props: {
										type: 'DeniedAlert',
										product: 'LeaveCal',
										appState: appState,
										latestTermsVersion: currentTermsVersion,
										denialRecord: latestAcceptance,
										denialUser: users.data.users.find((u: { userID: string }) => u.userID === latestAcceptance.userID),
										updateShownModal: updateShownModal,
									},
									size: 'md',
								})
								showTermsModal = true
							}
						} else {
							// they have an old version
							updateShownModal({
								modal: 'TermsAndConditionsAcceptance',
								props: {
									type: 'ChooseOption',
									product: 'LeaveCal',
									appState: appState,
									latestTermsVersion: currentTermsVersion,
									updateShownModal: updateShownModal,
								},
								size: 'md',
							})
							showTermsModal = true
						}
					} else {
						// never accepted so promp them too
						updateShownModal({
							modal: 'TermsAndConditionsAcceptance',
							props: {
								type: 'ChooseOption',
								product: 'LeaveCal',
								appState: appState,
								latestTermsVersion: currentTermsVersion,
								updateShownModal: updateShownModal,
							},
							size: 'md',
						})
						showTermsModal = true
					}
				}

				const showModal = (modal: ModalState) => {
					if (showTermsModal) {
						updatePendingModal(modal)
					} else {
						updateShownModal(modal)
					}
				}

				//We pick org and the calendar choosen by either the first one to come back or the selected
				//console.log('org', org, 'cal', cal, 'accountXeroOrgs.data.accountXeroOrgs', accountXeroOrgs.data.accountXeroOrgs)
				if (org || cal) {
					if (org) {
						updateSelectedOrg(org)
					}
					if (cal) {
						updateSelectedCalendar(cal)
					}
				} else if (accountXeroOrgs.data.accountXeroOrgs.length === 0 && I.am(appState.permission[appState.context]) === 'Admin') {
					showModal({
						modal: 'Connect',
						props: {
							xeroConsentUrl: xeroUrl.data.url,
							canUseKeypay: canAccountUseKeypay(currentAccount.data.accounts, appState!.authState!.attributes!['custom:currentAccountId']),
							keypayConsentUrl: keypayUrl.data.url,
							product: 'LeaveCal',
						},
						size: 'md',
					})
				} else if (calendars.data.calendars.length === 0) {
					const axo = accountXeroOrgs.data.accountXeroOrgs[0]
					updateSelectedOrg(axo)
					showModal({
						modal: 'Onboarding',
						props: {
							currentOrg: axo,
							accountXeroOrgID: axo.accountXeroOrgID,
							organisationID: axo.xeroOrg.xeroOrgID,
							organisationName: axo.xeroOrg.name,
							organisationVersion: axo.xeroOrg.version,
							appState: appState,
							firstCalendar: true,
						},
						size: 'lg',
					})
				} else if (accountXeroOrgs.data.accountXeroOrgs.length > 0 && calendars.data.calendars.length > 0) {
					if (localStorage.getItem('selectedOrgID')) {
						let selOrg = accountXeroOrgs.data.accountXeroOrgs.find(
							(org: AccountXeroOrg) => org.xeroOrg!.xeroOrgID === localStorage.getItem('selectedOrgID')
						)
						if (!selOrg) {
							selOrg = accountXeroOrgs.data.accountXeroOrgs[0]
							localStorage.setItem('selectedOrgID', selOrg.xeroOrg!.xeroOrgID)
						}

						updateSelectedOrg(selOrg)
						const calsForOrg = calendars.data.calendars.filter((cal: CalendarData) => cal.accountXeroOrgID === selOrg.accountXeroOrgID)
						const selectedCal = calsForOrg.find((cal: CalendarData) => cal.calendarID === localStorage.getItem('selectedCalendarID'))
						if (selectedCal) {
							updateSelectedCalendar(selectedCal)
						} else if (calsForOrg.length > 0) {
							updateSelectedCalendar(calsForOrg[0])
						} else {
							updateSelectedCalendar(null)
						}
						if (calsForOrg.length === 0) {
							showModal({
								modal: 'Onboarding',
								props: {
									accountXeroOrgID: selOrg.accountXeroOrgID,
									organisationID: selOrg.xeroOrg.xeroOrgID,
									organisationName: selOrg.xeroOrg.name,
									organisationVersion: selOrg.xeroOrg.version,
									appState: appState,
									firstCalendar: true,
								},
								size: 'lg',
							})
						}
					} else {
						const firstCreateTsOrg = accountXeroOrgs.data.accountXeroOrgs.sort((a: AccountXeroOrg, b: AccountXeroOrg) =>
							(b.createTs || '').localeCompare(a.createTs || '')
						)[0]
						updateSelectedOrg(firstCreateTsOrg)
						const calsForOrg = calendars.data.calendars.filter((cal: CalendarData) => cal.accountXeroOrgID === firstCreateTsOrg.accountXeroOrgID)
						updateSelectedCalendar(calsForOrg && calsForOrg.length !== 0 ? calsForOrg[0] : null)
						if (calsForOrg.length === 0) {
							showModal({
								modal: 'Onboarding',
								props: {
									accountXeroOrgID: firstCreateTsOrg.accountXeroOrgID,
									organisationID: firstCreateTsOrg.xeroOrg.xeroOrgID,
									organisationName: firstCreateTsOrg.xeroOrg.name,
									organisationVersion: firstCreateTsOrg.xeroOrg.version,
									appState: appState,
									firstCalendar: true,
								},
								size: 'lg',
							})
						}
					}
				}
			}

			if (appState.authState.isLoggedIn && appState.permission.leavecal) {
				fetchData()
			}
		},
		// eslint-disable-next-line
		[appState.authState, appState.permission.leavecal]
	)

	React.useEffect(
		() => {
			if (appState.authState.isLoggedIn) {
				refreshData()
			}
		},
		// eslint-disable-next-line
		[appState.authState]
	)

	React.useEffect(() => {
		if (shownModal.modal === '' && pendingModal.modal !== '') {
			updateShownModal(pendingModal)
			updatePendingModal(initialModalState)
		}
	}, [shownModal.modal]) // eslint-disable-line

	React.useEffect(() => {
		if (shownModal.modal === 'ChooseOrgCal') {
			updateShownModal({
				modal: 'ChooseOrgCal',
				props: {
					appState: appState,
					orgs: orgs,
					cals: calendars,
					currentOrg: selectedOrg,
					currentCal: selectedCalendar,
					updateCurrentOrg: updateSelectedOrg,
					updateCurrentCal: updateSelectedCalendar,
				},
				size: 'md',
			})
		}
	}, [selectedOrg, selectedCalendar]) // eslint-disable-line

	const updateCurrentCalendar = (cal: CalendarData) => {
		if (calendars) {
			const newCals = calendars.map((c) => {
				if (c.calendarID === cal.calendarID) {
					return cal
				} else {
					return c
				}
			})
			updateCalendars(newCals)
			updateSelectedCalendar(cal)
		}
	}

	const updateCurrentOrg = (org: AccountXeroOrg) => {
		if (selectedOrg) {
			updateSelectedOrg(org)
		}
	}

	// subscription
	const isTrialEnded =
		appState.subscription &&
		appState.subscription.isTrial &&
		appState.subscription.currentPeriodEnd &&
		DateTime.fromISO(appState.subscription.currentPeriodEnd).diffNow().toMillis() < 0 &&
		appState.subscription.cancelledAt &&
		DateTime.fromISO(appState.subscription.cancelledAt).diffNow().toMillis() > 0
	const isValid = appState.subscription && appState.subscription.valid

	return (
		<>
			<Row className="leavecal-container">
				<Col>
					<Row className="leavecal-blue-bar">
						<IndentedDropdownSelector
							organisations={orgs ? orgs : []}
							calendars={calendars ? calendars : []}
							selectedCal={selectedCalendar && selectedCalendar.calendarID ? selectedCalendar.calendarID : ''}
							selectedOrg={selectedOrg && selectedOrg.accountXeroOrgID ? selectedOrg.accountXeroOrgID : ''}
							updateSelectedOrg={updateSelectedOrg}
							updateSelectedCalendar={updateSelectedCalendar}
							appState={appState}
							refreshData={refreshData}
							updatePageStatus={updatePageStatus}
						/>
						{selectedOrg &&
						selectedOrg.userAccountXeroOrgAuth &&
						(selectedOrg.userAccountXeroOrgAuth.refreshToken === null || selectedOrg.userAccountXeroOrgAuth?.connectionID === null) ? (
							<Col sm="auto">
								<Badge variant="danger" className={'clickable-icon'}>
									{xeroConsentUrl && keypayConsentUrl ? (
										<a
											href={selectedOrg?.xeroOrg?.apiProvider === 'keypay' ? keypayConsentUrl : xeroConsentUrl}
											style={{ color: 'inherit' }}
										>
											Re-auth needed
										</a>
									) : (
										<span>Re-auth needed</span>
									)}
								</Badge>
							</Col>
						) : null}
						{(selectedOrg &&
							selectedOrg.xeroOrg &&
							selectedOrg.xeroOrg.products &&
							selectedOrg.xeroOrg.products.findIndex((p) => p.productID === XeroProducts.LeaveCalTrial.productID) >= 0) ||
						appState?.subscription?.isTrial ? (
							<Col sm="auto">
								<Badge variant="info" className={'clickable-icon'}>
									{I.have('admin', appState.permission[appState.context]) ? (
										<a href={'/billing'} style={{ color: 'black' }}>
											Trial Account
										</a>
									) : (
										<span style={{ color: 'black' }}>Trial Account</span>
									)}
								</Badge>
							</Col>
						) : null}
						<Col className="position-right" style={{ paddingRight: '10px', paddingTop: '8px' }}>
							<span>{`Last Sync: ${selectedCalendar && selectedOrg ? getUpdatedTs(selectedCalendar, selectedOrg) : 'Never'}`}</span>
						</Col>
						{selectedOrg && I.have('admin', appState.permission[appState.context]) ? (
							<Col sm="auto">
								<OverlayTrigger placement="auto" overlay={<Tooltip id={`tooltip-xero`}>Add new calendar.</Tooltip>}>
									<div>
										<Button
											type="button"
											onClick={() =>
												updateShownModal({
													modal: 'Onboarding',
													props: {
														accountXeroOrgID: selectedOrg.accountXeroOrgID,
														organisationID: selectedOrg.xeroOrg!.xeroOrgID,
														organisationName: selectedOrg.xeroOrg!.name,
														organisationVersion: selectedOrg.xeroOrg!.version,
														appState: appState,
														firstCalendar: calendars!.length === 0,
													},
													size: 'lg',
												})
											}
											style={{ cursor: 'pointer' }}
										>
											Add Calendar
										</Button>
									</div>
								</OverlayTrigger>
							</Col>
						) : null}
					</Row>
					<div className="leavecal-blue-bar"></div>
					{isTrialEnded && orgs && orgs.length > 0 ? (
						<div className="alert alert-danger mb-0" role="alert">
							Your trial has ended, your account will be shutdown on{' '}
							{DateTime.fromISO(appState.subscription?.cancelledAt || DateTime.now().toISO()).toLocaleString(DateTime.DATETIME_MED)} unless you{' '}
							<Link to="/billing">subscribe.</Link>
						</div>
					) : null}
					{appState.subscription && !isValid && !isTrialEnded && orgs && orgs.length > 0 && (
						<div className="alert alert-danger mb-0" role="alert">
							Your subscription is not active. Please <Link to="/billing">update your payment details</Link> to continue your service.
						</div>
					)}

					<ModalList
						handleClose={(...args: unknown[]) => {
							updateShownModal(initialModalState)
							if (shownModal.modal === 'Onboarding' && args.length > 0 && args[0] === 'refreshData') {
								const org = args[1] as AccountXeroOrg
								const cal = args[2] as CalendarData
								refreshData(org, cal)
							}
						}}
						show={shownModal.modal}
						data={shownModal.props}
						size={shownModal.size}
						style={
							shownModal.props && shownModal.props.type && shownModal.props.type === 'DeniedAlert'
								? { keyboard: true, backdrop: 'static' }
								: undefined
						}
						modals={[
							{
								id: 'ChooseOrgCal',
								body: OrgCalPicker,
							},
							{
								id: 'Connect',
								body: OnBoardModal,
							},
							{
								id: 'Onboarding',
								body: LeaveCalOnboardModal,
							},
							{
								id: 'TermsAndConditionsAcceptance',
								body: TermsAndConditionsModal,
							},
						]}
					/>
				</Col>
			</Row>

			<Row className="leavecal-body">
				<Col xs="auto" className="leavecal-left-menu">
					{I.have('admin', appState.permission[appState.context]) ? (
						<Row>
							<OverlayTrigger placement="auto" overlay={<Tooltip id={`tooltip-overview`}>Overview</Tooltip>}>
								<div className={'clickable'} onClick={() => updateFrameAndHash('overview')}>
									<img
										src={Icons.Overview}
										style={{
											width: '40px',
											filter: `${currentFrame === 'overview' ? 'opacity(1)' : 'opacity(0.6)'}`,
										}}
										alt="Overview icon"
									/>
								</div>
							</OverlayTrigger>
						</Row>
					) : null}
					<Row>
						<OverlayTrigger placement="auto" overlay={<Tooltip id={`tooltip-calendar`}>Calendar</Tooltip>}>
							<div className={'clickable'} onClick={() => updateFrameAndHash('calendar')}>
								<img
									src={Icons.Calendar}
									style={{
										width: '40px',
										filter: `${currentFrame === 'calendar' ? 'opacity(1)' : 'opacity(0.6)'}`,
									}}
									alt="Calendar icon"
								/>
							</div>
						</OverlayTrigger>
					</Row>
					{I.have('admin', appState.permission[appState.context]) && selectedOrg?.xeroOrg?.version === 'AU' ? (
						<Row>
							<OverlayTrigger placement="auto" overlay={<Tooltip id={`tooltip-unapproved`}>Unapproved Leave</Tooltip>}>
								<div className={'clickable'} onClick={() => updateFrameAndHash('unapprovedLeaves')}>
									<img
										src={Icons.UnapprovedLeave}
										style={{
											width: '40px',
											filter: `${currentFrame === 'unapprovedLeaves' ? 'opacity(1)' : 'opacity(0.6)'}`,
										}}
										alt="unapprovedLeaves icon"
									/>
								</div>
							</OverlayTrigger>
						</Row>
					) : null}
					{I.have('admin', appState.permission[appState.context]) && selectedOrg?.xeroOrg?.version === 'AU' ? (
						<Row>
							<OverlayTrigger placement="auto" overlay={<Tooltip id={`tooltip-unapproved`}>Leave Request Rules</Tooltip>}>
								<div className={'clickable'} onClick={() => updateFrameAndHash('leaveRules')}>
									<img
										src={Icons.UnapprovedLeaveRules}
										style={{
											width: '40px',
											filter: `${currentFrame === 'leaveRules' ? 'opacity(1)' : 'opacity(0.6)'}`,
										}}
										alt="leaveRules icon"
									/>
								</div>
							</OverlayTrigger>
						</Row>
					) : null}
					{I.have('admin', appState.permission[appState.context]) ? (
						<Row>
							<OverlayTrigger placement="auto" overlay={<Tooltip id={`tooltip-email`}>E-mail alerts</Tooltip>}>
								<div className={'clickable'} onClick={() => updateFrameAndHash('emails')}>
									<img
										src={Icons.Mail}
										style={{
											width: '40px',
											filter: `${currentFrame === 'emails' ? 'opacity(1)' : 'opacity(0.6)'}`,
										}}
										alt="E-Mail icon"
									/>
								</div>
							</OverlayTrigger>
						</Row>
					) : null}
					{I.have('admin', appState.permission[appState.context]) ? (
						<Row>
							<OverlayTrigger placement="auto" overlay={<Tooltip id={`tooltip-slack`}>Slack</Tooltip>}>
								<div className={'clickable'} onClick={() => updateFrameAndHash('slack')}>
									<img
										src={Icons.Slack}
										style={{
											width: '40px',
											filter: `${currentFrame === 'slack' ? 'opacity(1)' : 'opacity(0.6)'}`,
										}}
										alt="Slack icon"
									/>
								</div>
							</OverlayTrigger>
						</Row>
					) : null}
					<Row>
						<OverlayTrigger placement="auto" overlay={<Tooltip id={`tooltip-employees`}>Team Members</Tooltip>}>
							<div className={'clickable'} onClick={() => updateFrameAndHash('employees')}>
								<img
									src={Icons.Employee}
									style={{
										width: '40px',
										filter: `${currentFrame === 'employees' ? 'opacity(1)' : 'opacity(0.6)'}`,
									}}
									alt="Employees icon"
								/>
							</div>
						</OverlayTrigger>
					</Row>
					{I.have('admin', appState.permission[appState.context]) ? (
						<Row>
							<OverlayTrigger placement="auto" overlay={<Tooltip id={`tooltip-leaveBalances`}>Leave Balances</Tooltip>}>
								<div className={'clickable'} onClick={() => updateFrameAndHash('leaveBalances')}>
									<img
										src={Icons.Balance}
										style={{
											width: '40px',
											filter: `${currentFrame === 'leaveBalances' ? 'opacity(1)' : 'opacity(0.6)'}`,
										}}
										alt="Leave Balances icon"
									/>
								</div>
							</OverlayTrigger>
						</Row>
					) : null}
					{I.have('admin', appState.permission[appState.context]) ? (
						<Row>
							<OverlayTrigger placement="auto" overlay={<Tooltip id={`tooltip-settings`}>Settings</Tooltip>}>
								<div className={'clickable'} onClick={() => updateFrameAndHash('settings')}>
									<img
										src={Icons.Settings}
										style={{
											width: '40px',
											filter: `${currentFrame === 'settings' ? 'opacity(1)' : 'opacity(0.6)'}`,
										}}
										alt="Settings icon"
									/>
								</div>
							</OverlayTrigger>
						</Row>
					) : null}
					{/* this is to fill up the rest of the page */}
					<div style={{ flexGrow: 1 }}> </div>
				</Col>
				<Col>
					<BodyFrame
						appState={appState}
						updateAppState={updateAppState}
						pageStatus={pageStatus}
						currentFrame={currentFrame}
						currentOrg={selectedOrg}
						currentCalendar={selectedCalendar}
						xeroOrgCalendars={
							calendars && selectedOrg ? calendars.filter((cal: CalendarData) => cal.accountXeroOrgID === selectedOrg.accountXeroOrgID) : []
						}
						updateCurrentCalendar={updateCurrentCalendar}
						updateCurrentOrg={updateCurrentOrg}
						updatePageStatus={updatePageStatus}
						updateModal={() => {
							if (selectedOrg && selectedOrg.xeroOrg) {
								updateShownModal({
									modal: 'Onboarding',
									props: {
										accountXeroOrgID: selectedOrg.accountXeroOrgID,
										organisationID: selectedOrg.xeroOrg.xeroOrgID,
										organisationName: selectedOrg.xeroOrg.name,
										organisationVersion: selectedOrg.xeroOrg.version,
										appState: appState,
										firstCalendar: calendars!.length === 0,
									},
									size: 'lg',
								})
							}
						}}
					/>
				</Col>
			</Row>
		</>
	)
}

export default LeaveCal
